import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { auth } from "../firebaseConfig";
import { signOut } from "firebase/auth";
import { ref, onValue } from "firebase/database";
import { database } from "../firebaseConfig";
import TeamSwitcher from "../components/TeamSwitcher";
import HeaderNotesBadge from "./HeaderNotesBadge";
import useMyOpenTasks from "./tasks/useMyOpenTasks";
import "./tasks/HeaderTasksButton.css";
import { useActiveWorld } from "../hooks/useActiveWorld";
import { CREDIT_PLANS, resolveMembershipPlanId } from "../config/creditPlans";

const PLAN_BADGE_CONFIG = {
  starter: { label: 'STARTER', icon: '/img/salesgod_light.webp' },
  pro: { label: 'PRO', icon: '/img/globus.svg' },
  enterprise: { label: 'MAX', icon: '/img/promax.svg' },
};

const WorldDbHeader = ({ activeView, setActiveView, isLeadsOnlyAccess = false }) => {
  const navigate = useNavigate();
  const { userId: activeWorldId } = useActiveWorld();
  const myOpenTasks = useMyOpenTasks(activeWorldId);
  const myOverdueCount = myOpenTasks.filter(t => t.dueAt && t.dueAt < Date.now()).length;
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [userName, setUserName] = useState('No Name');
  const [userEmail, setUserEmail] = useState('No Email');
  const [userPhoto, setUserPhoto] = useState('');
  const [hasAgency, setHasAgency] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [hasDealWizard, setHasDealWizard] = useState(false);
  const [hasAcademy, setHasAcademy] = useState(false);
  const [membershipPlanId, setMembershipPlanId] = useState(null);

  // Prüfe ob wir auf einer externen Route sind (nicht /world)
  const isExternalRoute = !window.location.pathname.startsWith('/world');

  // Handler für Navigation - immer React Router verwenden (kein Page-Refresh)
  const handleNavClick = (view) => {
    navigate(`/world?view=${view}`, { replace: !isExternalRoute });
    if (!isExternalRoute) {
      setActiveView(view);
    }
  };

  const handleSignOut = () => {
    signOut(auth).then(() => {
      console.log("Erfolgreich abgemeldet!");
    }).catch((error) => {
      console.error("Fehler beim Abmelden:", error);
    });
  };

  useEffect(() => {
    if (auth.currentUser) {
      setUserEmail(auth.currentUser.email || 'No Email');
    }
  }, [auth.currentUser]);

  useEffect(() => {
    if (auth.currentUser) {
      setUserName(auth.currentUser.displayName || 'No Name');
      setUserEmail(auth.currentUser.email || 'No Email');
    }
  }, [auth.currentUser]);

  useEffect(() => {
    const userId = auth.currentUser?.uid;
    if (userId) {
      const userPhotoRef = ref(database, `Users/${userId}/userPhoto`);
      const userAgencyRef = ref(database, `Users/${userId}/hasAgency`);
      const userPremiumRef = ref(database, `Users/${userId}/isPremium`);

      onValue(userPhotoRef, (snapshot) => {
        if (snapshot.exists()) {
          setUserPhoto(snapshot.val());
        }
      });

      onValue(userAgencyRef, (snapshot) => {
        if (snapshot.exists()) {
          setHasAgency(snapshot.val());
        }
      });

      onValue(userPremiumRef, (snapshot) => {
        if (snapshot.exists()) {
          setIsPremium(snapshot.val());
        }
      });

      const userDealWizardRef = ref(database, `Users/${userId}/hasDealWizard`);
      onValue(userDealWizardRef, (snapshot) => {
        if (snapshot.exists()) {
          setHasDealWizard(snapshot.val());
        }
      });

      const userAcademyRef = ref(database, `Users/${userId}/hasAcademy`);
      onValue(userAcademyRef, (snapshot) => {
        if (snapshot.exists()) {
          setHasAcademy(snapshot.val());
        }
      });

      const userMembershipRef = ref(database, `Users/${userId}/membershipPlan`);
      const creditPlanRef = ref(database, `World/${userId}/creditPlan`);
      let membershipFlag = null;
      let creditPlanVal = null;
      const applyBadge = () => {
        setMembershipPlanId(resolveMembershipPlanId(creditPlanVal, { membershipPlan: membershipFlag }));
      };
      onValue(userMembershipRef, (snapshot) => {
        membershipFlag = snapshot.val() || null;
        applyBadge();
      });
      onValue(creditPlanRef, (snapshot) => {
        creditPlanVal = snapshot.exists() ? snapshot.val() : null;
        applyBadge();
      });
    }
  }, []);

  return (
    <div className="dashboard-header">
      <div className="logo-and-menu">
        <NavLink to="/" style={{ textDecoration: 'none'}}>
          <div className="header_logo_1">
            <img src="/img/BonusClub_Logo.webp" alt="" />
          </div>
        </NavLink>
        {membershipPlanId && PLAN_BADGE_CONFIG[membershipPlanId] && (
          <div className={`header_club_badge header_club_badge_${CREDIT_PLANS[membershipPlanId]?.emailPlan || 'standard'}`}>
            <span>{PLAN_BADGE_CONFIG[membershipPlanId].label}</span>
            <img src={PLAN_BADGE_CONFIG[membershipPlanId].icon} alt="" className="header_club_diamond" />
          </div>
        )}
        {/* Header notes – appears only when the account has "Header notes" saved in Notes & Files */}
        <HeaderNotesBadge worldId={activeWorldId} />
      </div>

      <div className="dashboard_header_links">
        {/* Targets - Nur für Owner/Full-Access */}
        {!isLeadsOnlyAccess && (
        <NavLink
          to="/world?view=tableOne"
          replace={!isExternalRoute}
          className={`menu-item ${activeView === "tableOne" ? "selector" : ""}`}
          onClick={() => { if (!isExternalRoute) setActiveView("tableOne"); }}
        >
          <div>
            <img className="invisible_icon" src="/img/target.svg" alt="" />
            <p>Targets <span>AI</span></p>
          </div>
        </NavLink>
        )}

        {/* Inbox - Nur für Owner/Full-Access */}
        {!isLeadsOnlyAccess && (
        <NavLink
          to="/world?view=smartInbox"
          replace={!isExternalRoute}
          className={`menu-item ${activeView === "smartInbox" ? "selector" : ""}`}
          onClick={() => { if (!isExternalRoute) setActiveView("smartInbox"); }}
        >
          <div>
            <img className="invisible_icon" src="/img/mailbox.svg" alt="" />
            <p>Inbox  <span>AI</span></p>
          </div>
        </NavLink>
        )}

        {/* Leadflow - Immer sichtbar */}
        <NavLink
          to="/world?view=leads"
          replace={!isExternalRoute}
          className={`menu-item ${activeView === "leads" ? "selector" : ""}`}
          onClick={() => { if (!isExternalRoute) setActiveView("leads"); }}
        >
          <div>
            <img className="invisible_icon" src="/img/hot.svg" alt="" />
            <p>Leads <span>AI</span></p>
          </div>
        </NavLink>
      </div>

      <div className="profile-section">
        {(myOpenTasks.length > 0 || activeView === 'tasks') && (
          <button
            type="button"
            className={`header-tasks-btn ${myOverdueCount > 0 ? 'header-tasks-btn--overdue' : ''} ${activeView === 'tasks' ? 'header-tasks-btn--active' : ''}`}
            onClick={() => navigate('/tasks')}
            title={`${myOpenTasks.length} open task${myOpenTasks.length !== 1 ? 's' : ''}${myOverdueCount ? ` · ${myOverdueCount} overdue` : ''}`}
          >
            <i className="fa fa-bolt"></i>
            {myOpenTasks.length > 0 && <span className="header-tasks-btn__count">{myOpenTasks.length}</span>}
          </button>
        )}

        {/* Settings-Icon - Nur für Owner/Full-Access */}
        {!isLeadsOnlyAccess && (
        <div 
          className="header-settings-icon" 
          onClick={() => handleNavClick("settings")}
          title="Settings"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        </div>
        )}
        
        <div className="profile-section-wrapper profile-section-wrapper--with-team" onClick={() => setShowProfileMenu(!showProfileMenu)}>
          {/* Own click scope: the account switcher closes the profile menu instead of toggling it */}
          <div
            className="profile-team-slot"
            onClick={(e) => {
              e.stopPropagation();
              setShowProfileMenu(false);
            }}
          >
            <TeamSwitcher />
          </div>
          <img 
            src="/img/menu_1.svg" 
            alt="Menu" 
            className={`profile-pic-2 ${showProfileMenu ? 'profile-pic-rotate' : ''}`}
          />
          <img 
            src={userPhoto || "/img/user-three.svg"}
            alt="Profile" 
            className="profile-pic"
          />
          {showProfileMenu && (
            <div className="profile-menu">
              <div className="profile-menu-info">
                <div className="profile-menu-item-2">{userName}</div>
                <div className="profile-menu-item-3">{userEmail}</div>
              </div>
              

              {isPremium && !hasAgency && (
                <div className="question-right-pro-6" style={{right: '135px', top: '8px'}}>
                  <img className="crown" src="/img/crow.svg" alt="Pro"/>
                  <p>PRO</p>
                </div>
              )}

              {!isLeadsOnlyAccess && (
              <button
                type="button"
                className="profile-menu-action"
                onClick={() => {
                  setShowProfileMenu(false);
                  handleNavClick("settings");
                }}
              >
                <span className="profile-menu-action-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                </span>
                <span>General Settings</span>
              </button>
              )}

              <NavLink to="/user-settings" className="profile-menu-action">
                <span className="profile-menu-action-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9"/>
                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                  </svg>
                </span>
                <span>Profile Settings</span>
              </NavLink>

              <a href="https://bonusclub.io" target="_blank" rel="noopener noreferrer" className="profile-menu-action">
                <span className="profile-menu-action-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16v16H4z"/>
                    <path d="m4 7 8 6 8-6"/>
                  </svg>
                </span>
                <span>Contact Us</span>
              </a>

              <NavLink to="/login" className="profile-menu-action" onClick={handleSignOut}>
                <span className="profile-menu-action-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                </span>
                <span>Sign Out</span>
              </NavLink>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorldDbHeader; 